import { useState, useEffect, useRef, useMemo } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Autocomplete,
  Typography,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  handleError,
  validateNonEmpty,
  getFirstPrincipalOwner,
  fetchProducts,
  isEditing,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "../../pages/edit/EditPage";
import useAgendaStore from "../../store/agendaStore";
import useNavigationHelperStore from "../../store/navigationHelperStore";
import ListItemWAvatarRemove from "../../layouts/ListItemWAvatarRemove";
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/en-gb';
import { useConfig } from '../../providers/ConfigProvider';

const EditAgenda = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { config, isLoadingConfig } = useConfig();
  const [products, setProducts] = useState([]);

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  // Use the agenda store
  const { 
    agenda,
    setAgenda, 
    updateAgenda, 
    resetAgenda
  } = useAgendaStore();

  // Use the navigation helper store
  const { from, state } = useNavigationHelperStore();

  const { updateAgendaFromLocation } = location.state || {};
  
  const [errors, setErrors] = useState({});
  const [refs] = useState({
    event_name: useRef(null),
    begin_time: useRef(null),
    end_time: useRef(null),
    description: useRef(null),
  });  

  useEffect(() => {
    // Handle initial agenda data loading
    if (updateAgendaFromLocation) {
      const agendaWithFormattedDates = {
        ...updateAgendaFromLocation,
        begin_time: dayjs(updateAgendaFromLocation.begin_time),
        end_time: updateAgendaFromLocation.end_time ? dayjs(updateAgendaFromLocation.end_time) : null
      };
      setAgenda(agendaWithFormattedDates);
    }
    
    updateTitle(
      '',
      isEditing(agenda) ? t('edit.editEvent') : t('edit.newEvent'),
      t('common.completeData')
    );

    toggleFab(false);

    // Process navigation data if coming from other screens
    if (location.state?.pet) {
      updateAgenda({
        pet: location.state.pet,
        owner: getFirstPrincipalOwner(location.state.pet.owners),
      });
    }

    if (location.state?.owner) {
      updateAgenda({
        owner: location.state.owner,
        pet: location.state.owner.pets?.[0] ?? null,
      });
    }

    if (location.state?.user) {
      updateAgenda({
        user: location.state.user,
      });
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const products = await fetchProducts();
        setProducts(products);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Add a delay to focus to ensure component is fully rendered
    setTimeout(() => {
      if (refs.event_name.current) {
        refs.event_name.current.focus();
      }
    }, 100);
  }, []);

  // Establecer usuario predeterminado
  useEffect(() => {
    if (!location.state?.user) {
      updateAgenda({ user: {name: config.name, id: config.id} });
    }
  }, [config]);
  
  // Search and remove functions for pet and owner
  const handleSearchPet = () => {
    navigate("/main/search-pet", { state: { from: "/main/edit-agenda" } });
  };

  const handleSearchOwner = () => {
    navigate("/main/search-owner", { state: { from: "/main/edit-agenda" } });
  };

  const handleSearchUser = () => {
    navigate("/main/search-user", { state: { from: "/main/edit-agenda" } });
  };

  const handleRemovePet = () => {
    updateAgenda({ pet: null });
  };

  const handleRemoveOwner = () => {
    updateAgenda({ owner: null });
  };

  const handleRemoveUser = () => {
    updateAgenda({ user: null });
  };  

  const handleChange = (e) => {
    const { name, value } = e.target;
    updateAgenda({
      [name]: value,
    });
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['event_name', 'begin_time', 'end_time', 'description'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateNonEmpty("event_name", agenda.event_name, setErrors, refs.event_name)) {
      return;
    }

    if (!agenda.user) {
      snackbar(t('edit.selectUser'));
      return;
    }

    if (agenda.owner && !agenda.pet) {
      snackbar(t('edit.selectPet', { name: config.vet.pet_naming_es.toLowerCase() }));
      return;
    }

    save();
  };

  const save = async () => {  
    setIsLoading(true);

    try {
      const dataToSend = {
        ...agenda,
        begin_time: dayjs(agenda.begin_time).format('YYYY-MM-DD HH:mm:ss'),
        end_time: agenda.end_time ? dayjs(agenda.end_time).format('YYYY-MM-DD HH:mm:ss') : null,
      };

      let response;
      if (isEditing(agenda)) {
        response = await axios.put(`${import.meta.env.VITE_API_URL}agenda`, 
          dataToSend, 
          {
            params: { id: agenda.id },
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            }
          });
      } else {
        response = await axios.post(`${import.meta.env.VITE_API_URL}agenda`, 
          dataToSend, 
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            }
          });
      }

      navigate(from, state);
      resetAgenda();
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isLoadingConfig}
    >

      <Box display="flex" gap={2} alignItems="center" sx={{ marginBottom: 2 }}>
        <ListItemWAvatarRemove
          primary={agenda.pet?.name || t('common.select')}
          onClick={handleSearchPet}
          onDelete={handleRemovePet}
          avatarContent={<PetsIcon />}
        />
        <ListItemWAvatarRemove
          primary={agenda.owner?.name || t('common.select')}
          onClick={handleSearchOwner}
          onDelete={handleRemoveOwner}
          avatarContent={<PersonIcon />}
        />
      </Box>

      {config.rol !== "USER" && config.vet.multiuser > 1 && (
        <>
          <Typography variant="caption">{t('edit.attendedBy')}</Typography>

          <ListItemWAvatarRemove
            primary={agenda.user?.name || t('common.select')}
            onClick={handleSearchUser}
            onDelete={handleRemoveUser}
            avatarContent={<PersonIcon />}
          />
        </>
      )}

      <Divider sx={{ marginTop: 2 }} />

      <Autocomplete
        fullWidth
        options={products}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option ? option.name : "";
        }}
        value={agenda.event_name}
        onChange={(e, newValue) => {
          if (newValue === null) {
            updateAgenda({ product: null });
          } else {
            updateAgenda({ product: { id: newValue.id, name: newValue.name } });
          }
        }}
        onInputChange={(event, newInputValue) => {
          updateAgenda({ event_name: newInputValue });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('edit.serviceEventName')}
            name="event_name"
            margin="normal"
            inputRef={refs.event_name}
            error={!!errors.event_name}
            helperText={errors.event_name}
            onKeyDown={(e) => handleKeyDown(e, 'event_name')}
          />
        )}
        freeSolo
      />

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
        <Box sx={{ display: 'flex', gap: 2, width: "100%", marginTop: 1 }}>
          <DateTimePicker
            label={t('edit.startDateTime')}
            value={agenda.begin_time}
            onChange={(newValue) => updateAgenda({ begin_time: newValue })}
            slotProps={{ 
              textField: { 
                fullWidth: true,
                inputRef: refs.begin_time,
                onKeyDown: (e) => handleKeyDown(e, 'begin_time')
              } 
            }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
          <DateTimePicker
            label={t('edit.endDateTime')}
            value={agenda.end_time}
            onChange={(newValue) => updateAgenda({ end_time: newValue })}
            slotProps={{ 
              textField: { 
                fullWidth: true,
                inputRef: refs.end_time,
                onKeyDown: (e) => handleKeyDown(e, 'end_time')
              } 
            }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </Box>
      </LocalizationProvider>

      <TextField
        fullWidth
        label={t('common.description')}
        name="description"
        value={agenda.description}
        onChange={handleChange}
        margin="normal"
        multiline
        rows={4}
        inputRef={refs.description}
        onKeyDown={(e) => handleKeyDown(e, 'description')}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(agenda.private_task)}
            onChange={(e) => updateAgenda({private_task: e.target.checked ? 1 : 0})}
            name="private_task"
            color="primary"
            size="medium"
          />
        }
        label={t('common.private')}
      />

    </EditPage>
  );
};

export default EditAgenda;
